import { useCallback, useEffect, useRef, useState } from "react";
import {
  captureException,
  ClientState,
  fetchConfigAndTranslations,
} from "@tree-company/discussit-embed";
import { LANGUAGE } from "../locales/index.js";
import { ThemeFlow } from "./ThemeFlow.jsx";
import { ProjectOverview } from "./ProjectOverview.jsx";
import { prefillAnswers } from "../prefill-answers.js";
import { fetchProject } from "../fetch-project.js";
import { alterSurveySettings } from "../alter-survey-settings.js";
import { voteOptions } from "../vote-options.js";
import { useIdleTimeout } from "../use-idle-timeout.js";
import { reloadPage } from "../reload-page.js";

export function App({ src, kiosk = false }) {
  const [discussits, setDiscussits] = useState(null);
  const [theme, setTheme] = useState(null);
  const [pending, setPending] = useState(null);
  const [atOverview, setAtOverview] = useState(true);
  const configsByKey = useRef(new Map());
  // Every pass through a discussit is a fresh reaction, but the visitor keeps
  // some answers. Deliberately in memory only: a reload starts over.
  const statesByKey = useRef(new Map());
  const lastOpenedKey = useRef(null);
  const passes = useRef(0);
  const overviewRef = useRef(null);

  useEffect(() => {
    const controller = new AbortController();

    fetchProject(src, { signal: controller.signal })
      .then(setDiscussits)
      .catch((caught) => {
        if (!controller.signal.aborted) captureException(caught);
      });

    return () => controller.abort();
  }, [src]);

  const returnToOverview = useCallback(() => setAtOverview(true), []);

  const hasOpenedTheme = theme !== null;

  // A reload also clears whatever the story player and embed keep in memory.
  // Waiting for a first open stops an untouched kiosk reloading every minute.
  useIdleTimeout({ enabled: kiosk && hasOpenedTheme, onIdle: reloadPage });

  useEffect(() => {
    const returning = atOverview === true && hasOpenedTheme === true;
    if (returning === false) return;

    const host = overviewRef.current?.getRootNode().host;
    if (!host) return;

    const { top } = host.getBoundingClientRect();
    if (top >= 0 && top <= window.innerHeight) return;

    host.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [atOverview, hasOpenedTheme]);

  async function open({ key, configUrl, storyUrl }) {
    setPending(key);

    try {
      if (!configsByKey.current.has(key)) {
        configsByKey.current.set(
          key,
          await fetchConfigAndTranslations(configUrl),
        );
      }
      const { config, translations } = configsByKey.current.get(key);

      // A kiosk is shared, so nothing may carry over between visitors.
      const state = new ClientState({
        apiUrl: config.base_url,
        apiToken: config.api_token,
        persistVisitor: !kiosk,
        initialAnswers: kiosk
          ? {}
          : prefillAnswers({
              sameDiscussit: statesByKey.current.get(key)?.answers,
              mostRecent: statesByKey.current.get(lastOpenedKey.current)
                ?.answers,
            }),
      });

      if (!kiosk) {
        statesByKey.current.set(key, state);
        lastOpenedKey.current = key;
      }
      passes.current += 1;

      setTheme({
        key,
        storyUrl,
        discussit: {
          pass: passes.current,
          config: {
            ...config,
            voteOptions,
            custom: { alterSurveySettings, showPreviousSectionButton: true },
          },
          translations,
          state,
        },
      });
      setAtOverview(false);
    } catch (caught) {
      // Nothing useful for the visitor to do with this, so keep it to ourselves.
      captureException(caught);
    } finally {
      setPending(null);
    }
  }

  return (
    <>
      {hasOpenedTheme && (
        <ThemeFlow
          theme={theme}
          hidden={atOverview}
          onCancel={returnToOverview}
        />
      )}
      {atOverview && discussits !== null && (
        <div className="app app--overview" lang={LANGUAGE} ref={overviewRef}>
          <div className="app__frame">
            <main className="app__main">
              <ProjectOverview
                discussits={discussits}
                pending={pending}
                onOpen={open}
                returning={hasOpenedTheme}
              />
            </main>
          </div>
        </div>
      )}
    </>
  );
}
